import { dependentKeyCompat } from '@ember/object/compat';
import { not, readOnly } from '@ember/object/computed';

import Model, { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import { trackedFunction } from 'reactiveweb/function';

import { saveHasManyRelationships } from 'umg-ember-backend/utils/model-save-hooks';
import {
  buildValidations,
  validator,
} from 'umg-ember-backend/utils/validations';

const Validations = buildValidations({
  contentType: validator('presence', true),
  product: [
    validator('belongs-to', {
      disabled: readOnly('model.isCustom'),
    }),
    validator('presence', {
      presence: true,
      disabled: readOnly('model.isCustom'),
    }),
    validator('has-external-value', {
      findByKey: 'externalType',
      findByValue: readOnly('model.productTargetType'),
      dependentKeys: ['productTargetType'],
      disabled: not('model.isProduct'),
      descriptionKey: 'forms.defaults.fields.product',
    }),
  ],
  productTargetType: validator('presence', {
    presence: true,
    disabled: readOnly('model.isCustom'),
  }),
  targets: [validator('has-many')],
});

export const settings = {
  saveHooks: {
    after: [saveHasManyRelationships('targets')],
  },
};

export default class ShortUrlModel extends Model.extend(Validations) {
  @attr('string') contentType;
  @attr('string') eventAction;
  @attr('string') internalRemarks;
  @attr('string') productTargetType;
  @attr('string') urlHash;
  @attr('string') utmCampaign;
  @attr('string') utmContent;

  // CMS fields
  @attr('number', { defaultValue: 2 }) cmsState;

  // Meta fields
  @attr cmsCreated;
  @attr cmsDeleted;
  @attr cmsLocktime;
  @attr cmsModified;
  @attr() cmsHistory;
  @belongsTo('user', { async: true, inverse: null }) cmsOwnerId;
  @belongsTo('user', { async: true, inverse: null }) cmsCreatorId;
  @belongsTo('user', { async: true, inverse: null }) cmsModifierId;
  @belongsTo('user', { async: true, inverse: null }) cmsLockerId;

  // Relations
  @belongsTo('product', { async: true, inverse: null }) product;
  @belongsTo('category', { async: true, inverse: null }) utmMedium;
  @belongsTo('category', { async: true, inverse: null }) utmSource;
  @hasMany('short-url-target', { async: true, inverse: 'shortUrl' }) targets;

  // Getter and setter
  get appendUtm() {
    const targets = this.resolvedTargets.value || [];
    return targets.every((target) => target.appendUtm);
  }

  get displayHeadline() {
    return this.urlHash;
  }

  get displayLabel() {
    return `https://umgt.de/${this.urlHash} (${this.productTargetType})`;
  }

  @dependentKeyCompat
  get isProduct() {
    return this.contentType === 'product';
  }

  @dependentKeyCompat
  get isCollection() {
    return this.contentType === 'collection';
  }

  @dependentKeyCompat
  get isCustom() {
    return this.contentType === 'custom';
  }

  @dependentKeyCompat
  get isProductShop() {
    return this.contentType === 'productshop';
  }

  get url() {
    return `https://umgt.de/${this.urlHash}`;
  }

  resolvedTargets = trackedFunction(this, async () => {
    return await this.targets;
  });
}
